import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

import DcPage, { useDcTheme } from "@/components/landing/dcv2/DcPage";
import { heroVideoFor } from "@/config/heroVideo";

/**
 * About AI2Aim.
 *
 * Restyled onto the v2 canvas language: the page wraps in <DcPage>, which
 * supplies the nav, the footer, the two families, the eleven custom properties
 * and the scroll runtime, and every section below is authored in the same
 * inline-style idiom as the homepage screens. Reveals are the shared runtime's
 * `data-rv` (80ms sibling stagger) and the decorative blooms are `data-plx`,
 * so this route no longer carries its own scroll listeners or observers.
 *
 * Copy, headings, ordering and destinations are unchanged from the previous
 * build. What follows is the record of what that build removed, kept here
 * because the absences are deliberate and a later editor would otherwise read
 * them as omissions:
 *
 * - The four headline stats (2025 / 11-50 / 6+ / 24/7). "11-50 Team Members"
 *   reads as scraped from a company directory and signals vendor risk to the
 *   buyer it was meant to reassure; "6+ Core Modules" contradicted the
 *   confirmed positioning by presenting the product as a taxonomy of parts.
 *   The founding year and country survive as prose, where they belong.
 * - The mission "quote". It carried typographic quote marks and no attribution
 *   - a quotation from nobody. It is now plainly the company's own sentence,
 *   in the company's own voice, on the company's own page.
 * - "Enterprise EMS & TMS". Unexpanded acronyms in a buyer-facing list.
 * - "Join the organizations leveraging AI2Aim...", which implied a customer
 *   base the page then declined to name.
 * - Both decorative images. `/about-hero.png` and `/about-mission.png` are
 *   glowing node-cloud renders - one with "HIVEMIND" baked into the pixels, so
 *   it could not scale, theme, translate, or be selected - and both are
 *   dark-only, so they fought the light theme. The argument this page has to
 *   make is structural, so it is drawn as an authored rail instead: real text,
 *   real tokens, both themes, and 1.6 MB lighter.
 *
 * A later pass rebuilt how the argument is drawn, without touching a sentence
 * of it. Copy, headings, section order and destinations are still what they
 * were; what changed is where the page puts them:
 *
 * - The principles list was a bare `minmax(0,17rem) minmax(0,1fr)` with no
 *   breakpoint. On a 375px viewport the label track took 263px and the body
 *   column resolved to 0px, so the longest section on the page rendered one
 *   word per line and off the right edge. The two-column split now lives in
 *   `.dc-defrow` behind a breakpoint, declared narrow-first.
 * - The page never used its own width. Hero, intro and engine all sat in a
 *   ~700px column inside a 1240px frame, leaving 45% of every wide viewport
 *   empty. The hero is now two columns. The engine went the other way and is
 *   capped: see EngineFigure for why width is not what made it emphatic.
 * - The eyebrow was a bare glyph. See Eyebrow.
 * - The engine was a card containing a card, which is the one shape that
 *   argues against "one product, not a suite". See EngineFigure.
 */

/* ─────────────────────── Canvas tokens ─────────────────────── */

const SANS = "'Manrope', sans-serif";
const MONO = "'Manrope', sans-serif";
const HAIRLINE = "1px solid rgba(var(--ink-rgb),.13)";
const RULE = "1px solid rgba(var(--ink-rgb),.2)";

/* Vertical padding only. The side gutter is `.site-gutter` on each section, so
   it can narrow on a phone (global.css, T101026002). */
const SECTION: React.CSSProperties = {
  paddingTop: 118,
  paddingBottom: 118,
  position: "relative",
  overflow: "clip",
};

const INNER: React.CSSProperties = {
  maxWidth: 1240,
  margin: "0 auto",
  position: "relative",
};

const H2: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: "clamp(28px, 3.4vw, 46px)",
  letterSpacing: "-0.02em",
  lineHeight: 1.14,
};

const H3: React.CSSProperties = {
  margin: 0,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: 18,
  letterSpacing: "-0.01em",
};

const BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  color: "rgba(var(--ink-rgb),.66)",
};

const CARD_BODY: React.CSSProperties = {
  margin: 0,
  fontSize: 14.5,
  color: "rgba(var(--ink-rgb),.66)",
};

const BTN_PRIMARY: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  background: "var(--btn)",
  color: "var(--btnfg)",
  padding: "15px 28px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  transition: "transform .2s, box-shadow .2s",
};

const BTN_GHOST: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  border: "1px solid rgba(var(--ink-rgb),.3)",
  color: "var(--ink)",
  padding: "15px 28px",
  borderRadius: 999,
  fontWeight: 600,
  fontSize: 15,
  transition: "border-color .2s, background .2s",
};

const INLINE_LINK: React.CSSProperties = {
  display: "inline-block",
  marginTop: 14,
  fontWeight: 600,
  fontSize: 14.5,
};

/**
 * The eyebrow, carrying the canvas's mark and the section's label.
 *
 * This used to be a bare glyph. The canvas's pattern is '✦ LABEL' and it is
 * used that way in a dozen places — Platform, Agents, Trust, HowItWorks and Cta
 * on the homepage, and Leadership, Careers and the legal pages beyond it. This
 * page carried the mark with the label stripped off, which made it the one
 * place the pattern appeared as decoration rather than as a scent marker, five
 * times over.
 *
 * The labels name the section. None of them asserts anything: they are the
 * page's own structure said out loud, which is the only kind of copy this page
 * may add.
 */
function Eyebrow({ label }: { label: string }) {
  return (
    <div
      style={{
        fontFamily: MONO,
        fontSize: 11.5,
        letterSpacing: ".16em",
        textTransform: "uppercase",
        color: "var(--link)",
        marginBottom: 18,
      }}
    >
      <span className="dc-mark" aria-hidden="true" /> {label}
    </div>
  );
}

/* ──────────────────────── The engine figure ──────────────────────── */

/**
 * The three named parts of the Intent Engine, in the order the mechanism runs,
 * each with its plate. Nothing here is a claim about results — each line says
 * what the part is.
 *
 * The plates are abstract material, never illustration of the product and never
 * people: a photograph of strangers on this page reads as AI2Aim's team, which
 * is the same fabrication as an invented testimonial, and is also the one case
 * the stock licences decline to cover — none of them guarantee a model release.
 *
 * Each one is chosen to carry the part's meaning as material rather than as
 * metaphor — sediment for what is retained, a structural lattice for what is
 * connected, directed light for what moves. They are downloaded desaturated
 * and tinted at runtime from theme tokens; see `.dc-plate` in styles.ts.
 *
 * Sources, all Unsplash licence (commercial use, no attribution required,
 * modification permitted). Recorded here because the licence not requiring
 * credit is not a reason for the repository to forget where a file came from:
 *   memory    images.unsplash.com/photo-1761847246648-565baaa9b030
 *   hivemind  images.unsplash.com/photo-1506815896118-1e3159bef72f
 *   agents    images.unsplash.com/photo-1618601208267-baa5b780b70e
 */
const PARTS = [
  {
    name: "Corporate Memory",
    body: "Holds what the organization knows: its documents, records, decisions, and the context around them. The platform is not starting from nothing every time it is asked something.",
    plate: "/plates/memory.jpg",
  },
  {
    name: "HiveMind",
    body: "Collective intelligence across that memory. It reasons over material that would otherwise sit in four systems that cannot see each other.",
    plate: "/plates/hivemind.jpg",
  },
  {
    name: "Role-based agents",
    body: "The part that acts. Agents scoped to a role carry out the work the reasoning points to: routing a request, matching an invoice, moving an approval forward. They act on the conclusion instead of only reporting it.",
    plate: "/plates/agents.jpg",
  },
];

/**
 * "One product, not a suite", drawn rather than asserted.
 *
 * The page's whole thesis is containment, and the previous build stated it in
 * prose and then drew something else: three bullets, and beneath them a card on
 * a short left rule. At a glance the module read as a fourth sibling — the one
 * reading the section exists to prevent.
 *
 * So the engine is a BOUNDARY, not a card. A hairline frame with its name
 * sitting on the top edge, the way a technical drawing labels an enclosure:
 * everything inside the rule is one thing. The module is the only real card
 * here, which is what lets it read as a component sitting inside the boundary
 * rather than as a second offering beside it. A card holding another card would
 * say the opposite — two peers, one nested by accident of layout.
 *
 * `variant` exists because the hero states the same shape before the section
 * explains it. Same component, twice, at two sizes: seeing the enclosure once
 * quietly and once in full is what turns "one product" from a sentence into
 * something the visitor has already looked at.
 */
function EngineFigure({ variant }: { variant: "mini" | "full" }) {
  const full = variant === "full";

  return (
    <div
      // The full figure's padding is `.about-engine-full` (global.css), so its
      // 34px sides can narrow on a phone (T101026002).
      className={full ? "about-engine-full" : undefined}
      style={{
        position: "relative",
        border: HAIRLINE,
        borderRadius: full ? 22 : 18,
        padding: full ? undefined : "40px 22px 22px",
        /* Capped, reversing an earlier note here that argued the thesis should
           take the full 1240px measure. The argument was right about emphasis
           and wrong about how emphasis is made: nothing inside this figure is
           wider than about 640px, so at full width every row ended in a few
           hundred pixels of nothing and the frame read as an empty box with
           text parked at its left edge. A figure is emphatic when it is dense,
           not when it is wide. */
        maxWidth: full ? 840 : undefined,
      }}
    >
      {/* The enclosure's name, sitting on the rule it interrupts. The paper
          swatch behind it is what makes the border read as broken rather than
          as passing underneath. */}
      <div
        style={{
          position: "absolute",
          top: full ? -12 : -11,
          left: full ? 26 : 20,
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          padding: "0 13px",
          background: "var(--paper)",
          fontFamily: MONO,
          fontSize: full ? 12 : 10.5,
          letterSpacing: ".16em",
          textTransform: "uppercase",
          color: "rgba(var(--ink-rgb),.62)",
          whiteSpace: "nowrap",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 7,
            height: 7,
            flex: "none",
            borderRadius: "50%",
            background: "var(--acc2)",
          }}
        />
        The Intent Engine
      </div>

      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "grid",
          gap: full ? 0 : 2,
        }}
      >
        {PARTS.map((part, i) => (
          <li
            key={part.name}
            // Each row is its own reveal, so the runtime's 80ms sibling stagger
            // plays the mechanism in the order it runs rather than dropping all
            // three in at once. This is the page's one authored moment.
            data-rv=""
            className={full ? "dc-engine-row" : undefined}
            // No `alignItems` here. `.dc-engine-row` sets `align-items: start`
            // and an inline value outranks it — with `baseline` the plate, which
            // holds no text, took its baseline from its bottom edge and dragged
            // every label down to sit level with the foot of its own image.
            style={{
              padding: full ? (i === 0 ? "0 0 22px" : "22px 0") : "7px 0",
              borderTop: full && i > 0 ? HAIRLINE : undefined,
            }}
          >
            {/* Decorative: the part is named and described in text beside it,
                so the plate carries no information of its own and announcing
                it would only put material with no meaning in the way. */}
            {full && (
              <div className="dc-plate">
                <img src={part.plate} alt="" aria-hidden="true" loading="lazy" />
              </div>
            )}
            {/* Title and body share ONE grid cell. They used to be two, in a
                three-column row of 88px / 15rem / 1fr, and at the figure's
                width that put a title of about 110px inside a 240px column and
                a 62ch body inside a column near 880px — roughly 450px of empty
                gutter on every row, most of it stranded between the name and
                the sentence describing it. */}
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: full ? 14 : 10,
                }}
              >
                {/* The numeral earns its place: these are steps, and the order
                    is the argument. Memory is what reasoning runs on, and
                    action is what reasoning concludes. */}
                <span
                  aria-hidden="true"
                  style={{
                    fontFamily: MONO,
                    fontSize: full ? 12.5 : 10.5,
                    color: "var(--link)",
                    flex: "none",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h4
                  style={{
                    ...H3,
                    fontSize: full ? 18 : 14.5,
                    color: "var(--ink)",
                  }}
                >
                  {part.name}
                </h4>
              </div>
              {full && (
                <p style={{ ...CARD_BODY, marginTop: 8, maxWidth: "68ch" }}>
                  {part.body}
                </p>
              )}
            </div>
          </li>
        ))}
      </ol>

      {/* The relationship, named once and drawn by the nesting.

          This was an elbow: a hairline descending, turning, and running right
          into the module, so the module read as reached THROUGH the engine
          rather than placed under it. Good idea, and it connected nothing to
          nothing. The stroke began at the top of its own box with no rail above
          it to come off — the list never had one — so it started in mid-air;
          and it turned right towards a card whose left edge was further LEFT
          than where the turn ended, so it pointed away from its target.

          The containment is already drawn, by the hairline enclosure this all
          sits inside. A second drawing of the same relationship was always
          going to be redundant even when it worked, so what is left is the word
          alone, on the shared left edge, captioning the card beneath it. */}
      <div data-rv="" style={{ marginTop: full ? 30 : 16 }}>
        <div
          style={{
            fontFamily: MONO,
            fontSize: full ? 11 : 9.5,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            // Measured, not guessed: .45 came out at 4.06:1 on dark paper and
            // .58 at 4.40:1 on light, both under the 4.5 floor for 11px. At
            // .66 it clears in both (5.75 light / 7.62 dark). This label names
            // the relationship the whole section argues; it is read, not
            // decoration, so it does not get to sit at decoration's contrast.
            color: "rgba(var(--ink-rgb),.66)",
            marginBottom: full ? 12 : 9,
          }}
        >
          contains
        </div>

        <div
          style={{
            // Flush with the rows and the caption above it. The indent went
            // with the elbow it was making room for, and without that the card
            // was simply the one thing in the figure not on its left edge.
            background: "var(--paper2)",
            border: HAIRLINE,
            borderRadius: full ? 14 : 11,
            padding: full ? 24 : "14px 16px",
          }}
        >
          <h4 style={{ ...H3, fontSize: full ? 18 : 14 }}>
            Employee Management System
          </h4>
          {full ? (
            <>
              <p style={{ ...CARD_BODY, marginTop: 8, maxWidth: "74ch" }}>
                Hiring pipelines, onboarding workflows, performance reviews,
                department and role management, and reporting and analytics. It
                runs on the same Corporate Memory as everything above it, which
                is what makes it a module rather than a second product. Task
                management and extra storage are add-ons to the same platform.
              </p>
              <Link to="/solutions" style={INLINE_LINK}>
                See what the platform does
              </Link>
            </>
          ) : (
            <p
              style={{
                ...CARD_BODY,
                marginTop: 5,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              Hiring, onboarding, reviews, and reporting, on the same memory.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── The hero backdrop ───────────────────────── */

/**
 * The hero's background loop, one clip per theme.
 *
 * Deliberately NOT the canvas's `useHeroVideo`. That hook tears the src out and
 * calls load() whenever the theme is dark, because the homepage answers dark
 * mode with a starfield and a 4K decoder running behind an invisible layer is
 * pure waste. This page has no starfield, and DcPage defaults to dark — reusing
 * that hook here would mean the default theme got no backdrop at all. Both
 * clips already exist with their own posters, so each theme plays its own.
 *
 * Everything below is a guard rather than an effect:
 *   - `preload="none"` and a poster, so the page costs a ~55KB still until the
 *     hero is actually on screen. The clips are 5-7MB.
 *   - Paused off screen, because a looping decoder behind four sections the
 *     visitor is reading is battery spent on something nobody is looking at.
 *   - Reduced motion never starts it. The canvas's global rule kills CSS
 *     animation but cannot touch a <video>, so this is the only thing standing
 *     between that preference and a moving background.
 */
function HeroBackdrop({ heroRef }: { heroRef: React.RefObject<HTMLElement> }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { dark } = useDcTheme();
  const { src, poster } = heroVideoFor(dark);

  useEffect(() => {
    const vid = videoRef.current;
    const hero = heroRef.current;
    if (!vid || !hero) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let onScreen = true;
    const sync = () => {
      if (onScreen) {
        // play() predates promises and returns undefined in some engines and in
        // jsdom, so the result is guarded before it is awaited. Autoplay can
        // also be refused outright; the poster reads correctly on its own, so
        // that rejection is swallowed rather than surfaced.
        const p = vid.play();
        if (p) void p.catch(() => {});
      } else {
        vid.pause();
      }
    };

    const io = new IntersectionObserver(
      (es) =>
        es.forEach((e) => {
          onScreen = e.isIntersecting;
          sync();
        }),
      { rootMargin: "80px" },
    );
    io.observe(hero);
    return () => io.disconnect();
  }, [heroRef, src]);

  return (
    <div
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}
    >
      <video
        ref={videoRef}
        // Keyed on src so a theme change remounts rather than leaving the
        // previous theme's first frame as the poster of the new clip.
        key={src}
        src={src}
        poster={poster}
        muted
        loop
        playsInline
        preload="none"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
      />
      {/* The scrim. Two layers doing different jobs: a flat wash that holds the
          whole section's contrast, and a horizontal ramp that keeps the left
          column on near-solid paper so the headline never has to fight the
          footage for legibility. */}
      <div style={{ position: "absolute", inset: 0, background: "var(--paper)", opacity: 0.72 }} />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(100deg, var(--paper) 0%, var(--paper) 34%, rgba(var(--ink-rgb),0) 78%)",
        }}
      />
    </div>
  );
}

/* ─────────────────────────── Section: hero ─────────────────────────── */

const HEADLINE = "A company built around one idea.";

function Hero() {
  const heroRef = useRef<HTMLElement>(null);

  return (
    <section
      ref={heroRef}
      className="site-gutter"
      style={{ ...SECTION, paddingTop: 104, paddingBottom: 92 }}
      aria-labelledby="about-hero-heading"
    >
      {/* The two decorative blooms that used to sit here are gone. The backdrop
          does the same atmospheric job and does it with the product's own
          footage; running both left the hero carrying a video, two glows, a
          headline, a paragraph, two buttons and a figure, which is not
          atmosphere, it is a pile. */}
      <HeroBackdrop heroRef={heroRef} />
      {/* Two columns once there is room. The previous build ran the headline,
          the paragraph and the buttons down a 900px column inside a 1240px
          frame and left the remaining 45% of every wide viewport empty — on a
          page whose first job is to show there is one thing here to look at. */}
      <div style={INNER} className="dc-about-hero">
        <div>
          <div data-rv="">
            <Eyebrow label="About AI2Aim" />
            <h1
              id="about-hero-heading"
              style={{
                margin: 0,
                fontFamily: SANS,
                fontWeight: 800,
                fontSize: "clamp(38px, 4.4vw, 60px)",
                lineHeight: 1.06,
                letterSpacing: "-0.025em",
                textWrap: "balance",
              }}
            >
              {HEADLINE}
            </h1>
          </div>

          <p
            data-rv=""
            style={{
              margin: "24px 0 0",
              maxWidth: "64ch",
              fontSize: 17,
              lineHeight: 1.65,
              color: "rgba(var(--ink-rgb),.7)",
            }}
          >
            AI2Aim Inc. started in Canada in 2025 around a question most
            operators have already answered for themselves: why does running the
            business take more effort than doing the work? We build one product,
            the Intent Engine, and everything we ship goes inside it.
          </p>

          <div
            data-rv=""
            style={{
              marginTop: 34,
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            <Link to="/contact" className="dc-h-btn-lg" style={BTN_PRIMARY}>
              Book a Demo
            </Link>
            <Link to="/about/ceo" className="dc-h-ghost" style={BTN_GHOST}>
              Meet our CEO
            </Link>
          </div>
        </div>

        {/* "One idea", shown while the headline says it. The section below
            explains each part; this states the shape and nothing more. It is
            not aria-hidden — the names are real content and a reader meeting
            them here is meeting them in the order the page argues them. */}
        {/* Lifted onto its own surface, because a hairline figure drawn at 13%
            ink cannot survive footage moving underneath it. The blur is what
            lets the backdrop still read as present behind the glass rather than
            being blocked out by an opaque panel. */}
        <div
          data-rv=""
          style={{
            background: "color-mix(in srgb, var(--paper) 74%, transparent)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            borderRadius: 22,
            padding: 10,
          }}
        >
          <EngineFigure variant="mini" />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Section: story ─────────────────────────── */

const STORY = [
  "Every company we looked at was running the same shape of problem. People operations in one system, the books in another, work tracked in a third, and the documents that explain any of it scattered across all of them. Nothing knew what the others knew, so staff became the integration layer: re-keying, chasing, and checking whether something had already been done.",
  "The usual answer is to buy another system, which adds a fifth place to look. We took the other route: build one platform that holds the organization's knowledge, reasons across it, and acts on it. Not a suite of products that share a logo. One product, with the parts of the business as modules inside it.",
  // Was "a small team in Edmonton, Alberta", which read as a single-location
  // company and contradicted /about/ceo once that page's record was corrected
  // to three offices across the Philippines, Canada and India. The countries
  // are named and the cities are not: Edmonton is confirmed by the street
  // address on /contact, but no city is confirmed for the other two, and
  // inventing one would be the kind of plausible-looking detail PRODUCT.md
  // bars. "Small team" survives because it is the honest framing a skeptical
  // buyer is being given, not a hedge.
  "We are a small team working across the Philippines, Canada and India, building for organizations that have to defend the tools they choose. That shapes what we ship and what we say about it.",
];

function StorySection() {
  return (
    <section className="site-gutter" style={SECTION} aria-labelledby="story-heading">
      <div
        style={{
          ...INNER,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
          gap: 64,
          alignItems: "start",
        }}
      >
        <div data-rv="">
          <Eyebrow label="Origin" />
          <h2 id="story-heading" style={H2}>
            Why we started.
          </h2>
          {/* The company's own sentence, in its own voice. It used to sit in
              typographic quote marks with nothing to attribute it to. */}
          <p
            style={{
              margin: "22px 0 0",
              fontFamily: SANS,
              fontWeight: 600,
              fontSize: "clamp(19px, 1.9vw, 24px)",
              lineHeight: 1.3,
              letterSpacing: "-0.01em",
              color: "var(--ink)",
            }}
          >
            Traditional software creates silos. We build a system that
            remembers, reasons, and acts.
          </p>
        </div>

        <div data-rv="" style={{ display: "grid", gap: 22 }}>
          {STORY.map((paragraph) => (
            <p
              key={paragraph.slice(0, 28)}
              style={{ ...BODY, maxWidth: "68ch", lineHeight: 1.75 }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ────────────────────── Section: one product ────────────────────── */

/**
 * "One product, not a suite", drawn rather than asserted.
 *
 * The section's whole job is to make containment visible, so the figure does
 * the arguing and the prose only introduces it. See EngineFigure above for why
 * the engine is a boundary rather than a card, and why the module is the only
 * card inside it.
 */
function OneProductSection() {
  return (
    <section className="site-gutter" style={SECTION} aria-labelledby="one-product-heading">
      <div style={INNER}>
        <div data-rv="" style={{ maxWidth: "62ch", marginBottom: 52 }}>
          <Eyebrow label="Product Architecture" />
          <h2 id="one-product-heading" style={{ ...H2, marginBottom: 16 }}>
            One product, not a suite.
          </h2>
          <p style={BODY}>
            AI2Aim ships a single platform. What a vendor would usually sell you
            as a separate product, we build as a module inside this one, so
            there is one place the organization&rsquo;s knowledge lives and one
            system acting on it.
          </p>
        </div>

        {/* No max-width. This is the page's thesis and it gets the full
            measure; the previous build capped it at 1000px inside a 1240px
            frame, which left the section that matters most looking like the
            narrowest thing on the page. */}
        <EngineFigure variant="full" />
      </div>
    </section>
  );
}

/* ────────────────────── Section: how we work ────────────────────── */

/**
 * Presented as a ruled definition list rather than four identical cards. Each
 * of these is checkable against what this site actually does, which is the
 * point of putting them here.
 */
const PRINCIPLES = [
  {
    name: "Mechanism before adjectives.",
    body: "Being AI-powered is not an argument. We would rather show how intent becomes an outcome, which is why Corporate Memory, HiveMind, and role-based agents are named parts you can ask hard questions about.",
  },
  {
    name: "One product, not a portfolio.",
    body: "Anything we build has to belong inside the Intent Engine. When a capability needs its own separate story to make sense, that is a sign we have not finished thinking it through.",
  },
  {
    name: "Claims we can stand behind.",
    body: "We publish certifications once the audit is complete and the report exists, rather than describing ourselves as ready for one. Where we do not have a figure yet, you will find nothing in its place rather than an estimate.",
  },
  {
    name: "Built to be inspected.",
    body: "Evaluating a platform means scrutinising how it handles your data before procurement is ever involved. Our security position is written down, and we send full documentation for a review.",
    link: { to: "/security", label: "Read our security position" },
  },
];

function HowWeWorkSection() {
  return (
    <section className="site-gutter" style={SECTION} aria-labelledby="how-we-work-heading">
      <div style={INNER}>
        <div data-rv="" style={{ maxWidth: 640, marginBottom: 56 }}>
          <Eyebrow label="Principles" />
          <h2 id="how-we-work-heading" style={H2}>
            How we work.
          </h2>
        </div>

        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            borderTop: RULE,
          }}
        >
          {PRINCIPLES.map((principle) => (
            <li
              key={principle.name}
              data-rv=""
              // The two-column split now lives in `.dc-defrow`, behind a
              // breakpoint. Declared inline and unconditionally, the label
              // track took 263px of a 375px viewport and the body column
              // resolved to 0px — every paragraph here rendered one word per
              // line and ran off the right edge of the phone.
              className="dc-defrow"
              style={{
                borderBottom: RULE,
                padding: "30px 4px",
              }}
            >
              <h3 style={H3}>{principle.name}</h3>
              <div style={{ maxWidth: "66ch" }}>
                <p style={{ ...BODY, fontSize: 15.5, lineHeight: 1.7 }}>
                  {principle.body}
                </p>
                {principle.link && (
                  <Link to={principle.link.to} style={INLINE_LINK}>
                    {principle.link.label}
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ─────────────────────────── Section: close ─────────────────────────── */

/**
 * The close, on the canvas's dark band so the page lands where the homepage
 * lands. The previous copy read "Join the organizations leveraging AI2Aim",
 * which asserted a customer base on a page that names none — the one claim a
 * skeptical evaluator would check first. It offers the conversation itself,
 * which is the thing actually on the table.
 */
function CloseSection() {
  return (
    <section
      className="dc-seam dc-seam-foot"
      style={{
        position: "relative",
        overflow: "clip",
        background: "var(--band)",
        color: "var(--band-ink)",
      }}
      aria-labelledby="about-close-heading"
    >
      {/* Not parallaxed: it is anchored to the section's midpoint. */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          right: -220,
          width: 640,
          height: 640,
          marginTop: -320,
          border: "1px solid rgba(var(--band-acc-rgb),.18)",
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />

      <div
        className="site-gutter"
        style={{
          maxWidth: 1240,
          margin: "0 auto",
          paddingTop: 128,
          paddingBottom: 110,
          position: "relative",
          textAlign: "center",
        }}
      >
        <h2
          data-rv=""
          id="about-close-heading"
          style={{
            margin: "0 0 18px",
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: "clamp(34px, 4.4vw, 60px)",
            letterSpacing: "-0.02em",
            lineHeight: 1.08,
          }}
        >
          Talk to the people building it.
        </h2>

        <p
          data-rv=""
          style={{
            margin: "0 auto 36px",
            maxWidth: 620,
            fontSize: 17,
            color: "rgba(var(--band-ink-rgb),.7)",
          }}
        >
          We would rather walk you through the mechanism than send a brochure.
          Bring the hardest question you have about how this would work inside
          your organization, including the ones we do not have an answer for
          yet.
        </p>

        <div
          data-rv=""
          style={{
            display: "flex",
            justifyContent: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/contact"
            className="dc-h-btn-cyan"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "rgb(var(--band-acc-rgb))",
              color: "var(--band-on-acc)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 15.5,
              transition: "transform .2s, box-shadow .2s",
            }}
          >
            Book a Demo
          </Link>
          <Link
            to="/about/ceo"
            className="dc-h-ghost-light"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              border: "1px solid rgba(var(--band-ink-rgb),.3)",
              color: "var(--band-ink)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 600,
              fontSize: 15.5,
              transition: "border-color .2s, background .2s",
            }}
          >
            Meet our CEO
          </Link>
        </div>

        {/* Says plainly why this page names no customers, so the absence reads
            as a policy rather than as something missing. */}
        <p
          data-rv=""
          style={{
            margin: "38px auto 0",
            maxWidth: 620,
            fontSize: 13.5,
            lineHeight: 1.7,
            color: "rgba(var(--band-ink-rgb),.62)",
          }}
        >
          Customer names and references are published only once the customer has
          approved them. Where you would expect to find them on this site, you
          will see a slot marked as empty rather than a stand-in.
        </p>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <DcPage>
      <Hero />
      <StorySection />
      <OneProductSection />
      <HowWeWorkSection />
      <CloseSection />
    </DcPage>
  );
}
